import { lazy, Suspense, type ComponentType } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import {
  GuardaOnboarding,
  RotaDeGestao,
  RotaDeModulo,
  RotaProtegida,
} from "@/components/layout/RotaProtegida";
import { Carregando } from "@/components/layout/Carregando";
import { Providers } from "@/app/providers";
import { RotaDeAdmin } from "@/features/admin/components/RotaDeAdmin";
import { GuardaAssinatura } from "@/features/billing/components/GuardaAssinatura";
import { Raiz } from "@/app/Raiz";

/**
 * Cada tela fora da landing vira um arquivo proprio, baixado so quando a rota
 * abre. Antes o visitante da landing baixava o sistema inteiro (1,1 MB de JS)
 * para ler uma pagina de vendas, e isso pesa no Core Web Vitals e no Google.
 */
function tela<P extends object>(carregar: () => Promise<ComponentType<P>>) {
  return lazy(() => carregar().then((c) => ({ default: c })));
}

const AppShell = tela(() => import("@/components/layout/AppShell").then((m) => m.AppShell));
const AdminPage = tela(() => import("@/features/admin/pages/AdminPage").then((m) => m.AdminPage));
const AuthPage = tela(() => import("@/features/auth/pages/AuthPage").then((m) => m.AuthPage));
const ConvitePage = tela(() =>
  import("@/features/auth/pages/ConvitePage").then((m) => m.ConvitePage),
);
const OnboardingPage = tela(() =>
  import("@/features/auth/pages/OnboardingPage").then((m) => m.OnboardingPage),
);
const RedefinirSenhaPage = tela(() =>
  import("@/features/auth/pages/RedefinirSenhaPage").then((m) => m.RedefinirSenhaPage),
);
const BemVindoPage = tela(() =>
  import("@/features/onboarding/pages/BemVindoPage").then((m) => m.BemVindoPage),
);
const AtendimentoPage = tela(() =>
  import("@/features/atendimento/pages/AtendimentoPage").then((m) => m.AtendimentoPage),
);
const AutomacoesPage = tela(() =>
  import("@/features/automacoes/pages/AutomacoesPage").then((m) => m.AutomacoesPage),
);
const ServicosPage = tela(() =>
  import("@/features/servicos/pages/ServicosPage").then((m) => m.ServicosPage),
);
const AfiliadosPage = tela(() =>
  import("@/features/afiliados/pages/AfiliadosPage").then((m) => m.AfiliadosPage),
);
const LegalPage = tela(() => import("@/features/legal/pages/LegalPage").then((m) => m.LegalPage));
const LojaPage = tela(() => import("@/features/loja/pages/LojaPage").then((m) => m.LojaPage));
const ProdutoDaVitrinePage = tela(() =>
  import("@/features/vitrine/pages/ProdutoDaVitrinePage").then((m) => m.ProdutoDaVitrinePage),
);
const VitrinePage = tela(() =>
  import("@/features/vitrine/pages/VitrinePage").then((m) => m.VitrinePage),
);
const ConfiguracoesPage = tela(() =>
  import("@/features/configuracoes/pages/ConfiguracoesPage").then((m) => m.ConfiguracoesPage),
);
const ContatosPage = tela(() =>
  import("@/features/contatos/pages/ContatosPage").then((m) => m.ContatosPage),
);
const RelatoriosPage = tela(() =>
  import("@/features/relatorios/pages/RelatoriosPage").then((m) => m.RelatoriosPage),
);
const VendasPage = tela(() =>
  import("@/features/vendas/pages/VendasPage").then((m) => m.VendasPage),
);

/**
 * O app mora na raiz do dominio (samev.com.br), sem subdominio.
 * `/` e publico e decide pela sessao: landing para visitante, atendimento para
 * quem esta logado.
 */
export function App() {
  return (
    <Providers>
      <BrowserRouter>
        <Suspense fallback={<Carregando />}>
          <Routes>
            <Route path="/" element={<Raiz />} />
            {/* A landing de redirecionadores saiu: o Samev e para qualquer negocio. */}
            <Route path="/redirecionadores-e-shoppers" element={<Navigate to="/" replace />} />
            <Route path="/entrar" element={<AuthPage />} />
            <Route path="/criar-conta" element={<AuthPage modo="criar" />} />
            <Route path="/comecar" element={<OnboardingPage />} />
            <Route path="/convite/:token" element={<ConvitePage />} />
            {/* Fora das rotas protegidas: o link do e-mail e a unica credencial
              de quem chega aqui. */}
            <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
            <Route path="/bem-vindo" element={<BemVindoPage />} />
            {/* Vitrine publica, sem login. O robo de preview do WhatsApp nem chega
              aqui: o nginx o manda para a function `vitrine-og`. */}
            <Route path="/l/:loja" element={<VitrinePage />} />
            <Route path="/l/:loja/:produto" element={<ProdutoDaVitrinePage />} />

            <Route path="/termos" element={<LegalPage documento="termos" />} />
            <Route path="/privacidade" element={<LegalPage documento="privacidade" />} />
            <Route path="/cookies" element={<LegalPage documento="cookies" />} />
            <Route path="/termos-afiliados" element={<LegalPage documento="afiliados" />} />
            {/* Fora das rotas protegidas: o afiliado nao precisa ter empresa. */}
            <Route path="/afiliados" element={<AfiliadosPage />} />

            <Route element={<RotaProtegida />}>
              {/* Painel da plataforma: fora do onboarding e do bloqueio por
                assinatura, porque quem opera o Samev precisa entrar mesmo com a
                propria empresa vencida. */}
              <Route element={<RotaDeAdmin />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>

              <Route element={<GuardaOnboarding />}>
                <Route element={<GuardaAssinatura />}>
                  <Route element={<AppShell />}>
                    <Route path="/atendimento" element={<AtendimentoPage />} />
                    <Route path="/atendimento/:conversaId" element={<AtendimentoPage />} />
                    <Route path="/contatos" element={<ContatosPage />} />
                    {/* Aberta ao atendente: as abas de gestao sao barradas dentro da pagina. */}
                    <Route element={<RotaDeModulo modulo="loja" />}>
                      <Route path="/loja/*" element={<LojaPage />} />
                    </Route>
                    <Route element={<RotaDeModulo modulo="servicos" />}>
                      <Route path="/servicos" element={<ServicosPage />} />
                    </Route>
                    {/* Gestao: o menu esconde, mas a rota tambem barra. Sem isso,
                  o atendente chega digitando a URL. */}
                    <Route element={<RotaDeGestao />}>
                      <Route path="/vendas" element={<VendasPage />} />
                      <Route path="/relatorios" element={<RelatoriosPage />} />
                      <Route path="/automacoes/*" element={<AutomacoesPage />} />
                      <Route path="/configuracoes/*" element={<ConfiguracoesPage />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </Providers>
  );
}
